import React, { useMemo, useState } from 'react';
import { OverallSalesHistoryRow, OverallSalesHistoryMetrics } from '@/types/asinSalesHistory';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TrendingUp, TrendingDown, Star, ArrowUpRight, ArrowDownRight } from 'lucide-react';

interface OverallQuickInsightsProps {
  data: OverallSalesHistoryRow[];
  metrics: OverallSalesHistoryMetrics | null;
  loading: boolean;
}

export const OverallQuickInsights: React.FC<OverallQuickInsightsProps> = ({
  data,
  metrics,
  loading
}) => {
  const [selectedDay, setSelectedDay] = useState<any>(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCompact = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return formatCurrency(value);
  };

  const formatPercent = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const insights = useMemo(() => {
    if (!data.length || !metrics) return {
      bestDay: null,
      highestRevenue: null,
      highestConversionRate: null
    };

    // Aggregate data by date
    const dayMap = new Map<string, {
      date: Date;
      totalRevenue: number;
      totalGrossProfit: number;
      totalPPCSpend: number;
      totalUnits: number;
      totalTraffic: number;
      conversions: number;
      dataPoints: number;
    }>();

    data.forEach(row => {
      const dateKey = row.date.toISOString().split('T')[0];
      const existing = dayMap.get(dateKey) || {
        date: row.date,
        totalRevenue: 0,
        totalGrossProfit: 0,
        totalPPCSpend: 0,
        totalUnits: 0,
        totalTraffic: 0,
        conversions: 0,
        dataPoints: 0
      };

      existing.totalRevenue += row.revenue;
      existing.totalGrossProfit += row.grossProfit;
      existing.totalPPCSpend += row.ppcSpend;
      existing.totalUnits += row.units;
      existing.totalTraffic += row.traffic;
      existing.conversions += row.traffic * (row.conversionRate / 100);
      existing.dataPoints += 1;

      dayMap.set(dateKey, existing);
    });

    const dayData = Array.from(dayMap.values()).map(item => ({
      ...item,
      averageConversionRate: item.totalTraffic > 0 ? (item.conversions / item.totalTraffic) * 100 : 0,
      profitMargin: item.totalRevenue > 0 ? (item.totalGrossProfit / item.totalRevenue) * 100 : 0,
      acos: item.totalRevenue > 0 ? (item.totalPPCSpend / item.totalRevenue) * 100 : 0
    }));

    // Filter out days with no traffic for conversion rate calculation
    const dayDataWithTraffic = dayData.filter(item => item.totalTraffic > 0);

    // Best performing day (by gross profit)
    const bestDay = dayData.length > 0 ? dayData.reduce((best, current) =>
      current.totalGrossProfit > best.totalGrossProfit ? current : best
    , dayData[0]) : null;

    // Highest revenue day
    const highestRevenue = dayData.length > 0 ? dayData.reduce((best, current) =>
      current.totalRevenue > best.totalRevenue ? current : best
    , dayData[0]) : null;

    // Highest conversion rate day (only among those with traffic)
    const highestConversionRate = dayDataWithTraffic.length > 0
      ? dayDataWithTraffic.reduce((best, current) =>
          current.averageConversionRate > best.averageConversionRate ? current : best
        , dayDataWithTraffic[0])
      : null;

    return {
      bestDay,
      highestRevenue,
      highestConversionRate
    };
  }, [data, metrics]);

  const handleCardClick = (day: any) => {
    setSelectedDay(day);
    setShowDetailPanel(true);
  };

  const CompactDayCard = ({ day, title, metricValue, bgClass, iconClass, icon: Icon, metricColor = "text-gray-900 dark:text-gray-100" }: any) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Card
            className={`${bgClass} cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.01] h-24`}
            onClick={() => day && handleCardClick(day)}
          >
            <CardContent className="p-3 h-full flex flex-col justify-between">
              {/* Header with icon and title */}
              <div className="flex items-center space-x-2 mb-2">
                <Icon className={`h-4 w-4 ${iconClass}`} />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {title}
                </span>
              </div>

              {loading ? (
                <div className="space-y-1">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-3 w-16" />
                </div>
              ) : day ? (
                <div className="space-y-1">
                  {/* Date + Value on same line */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                      {formatDate(day.date)}
                    </span>
                    <span className={`text-sm font-bold ${metricColor}`}>
                      {metricValue}
                    </span>
                  </div>

                  {/* Click instruction - centered */}
                  <div className="text-center">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Click to see more information
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center justify-center h-full">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </TooltipTrigger>
        <TooltipContent>
          <p>Click for detailed view</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Card 1: Best Performing Day */}
        <CompactDayCard
          title="Best Performing Day"
          bgClass="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800"
          iconClass="text-emerald-600 dark:text-emerald-400"
          icon={TrendingUp}
          day={insights.bestDay}
          metricValue={insights.bestDay ? formatCurrency(insights.bestDay.totalGrossProfit) : "N/A"}
          metricColor="text-emerald-600 dark:text-emerald-400"
        />
        
        {/* Card 2: Highest Revenue Day */}
        <CompactDayCard
          title="Highest Revenue Day"
          bgClass="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
          iconClass="text-blue-600 dark:text-blue-400"
          icon={Star}
          day={insights.highestRevenue}
          metricValue={insights.highestRevenue ? formatCurrency(insights.highestRevenue.totalRevenue) : "N/A"}
          metricColor="text-blue-600 dark:text-blue-400"
        />

        {/* Card 3: Highest Conversion Rate Day */}
        <CompactDayCard
          title="Best Conversion Day"
          bgClass="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800"
          iconClass="text-purple-600 dark:text-purple-400"
          icon={ArrowUpRight}
          day={insights.highestConversionRate}
          metricValue={insights.highestConversionRate ? formatPercent(insights.highestConversionRate.averageConversionRate) : "N/A"}
          metricColor="text-purple-600 dark:text-purple-400"
        />
      </div>

      {/* Detail Panel Dialog */}
      <Dialog open={showDetailPanel} onOpenChange={setShowDetailPanel}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Day Details: {selectedDay && formatDate(selectedDay.date)}</DialogTitle>
          </DialogHeader>
          {selectedDay && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Revenue:</span>
                  <span className="ml-2 font-medium">{formatCurrency(selectedDay.totalRevenue)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Gross Profit:</span>
                  <span className="ml-2 font-medium text-green-600">{formatCurrency(selectedDay.totalGrossProfit)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Profit Margin:</span>
                  <span className="ml-2 font-medium">{formatPercent(selectedDay.profitMargin)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Units Sold:</span>
                  <span className="ml-2 font-medium">{selectedDay.totalUnits}</span>
                </div>
                <div>
                  <span className="text-gray-600">ACoS:</span>
                  <span className="ml-2 font-medium">{formatPercent(selectedDay.acos)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Conversion Rate:</span>
                  <span className="ml-2 font-medium">{formatPercent(selectedDay.averageConversionRate)}</span>
                </div>
              </div>
              
              {/* Mini Chart Placeholder */}
              <div className="mt-4">
                <h4 className="text-sm font-medium mb-2">Daily Breakdown</h4>
                <div className="h-16 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center">
                  <span className="text-xs text-gray-500">Daily Performance Summary</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OverallQuickInsights;